import {
  BooleanRelations,
  ClientRowFilter,
  DateRelations,
  INVALID_COLUMN_NAME_ERROR_MESSAGE,
  INVALID_SECOND_VALUE_ERROR_MESSAGE,
  INVALID_VALUE_ERROR_MESSAGE,
  NumberRelations,
  StringRelations,
} from "@avenueops/shared-types";
import { ColumnType, Relation } from "@prisma/client";
import _ from "lodash";
import { FieldValues, UseFormClearErrors, UseFormSetError } from "react-hook-form";

// Below are assorted helper functions for the component RowFilterItem

/**
 * Helper for updating the column type. This requires additional logic because in some cases
 * by changing the column type we'll have to change the relation and set the first or second value to undefined.
 */
export function updateColumnType(
  columnType: ColumnType,
  relation: Relation,
  onChange: (_: Partial<ClientRowFilter>) => void,
  needsFirstValue: (_: Relation) => boolean,
  needsSecondValue: (_: Relation) => boolean
) {
  const availableRelations = getRelations(columnType);
  const newRelation = new Set(availableRelations).has(relation) ? relation : availableRelations[0];
  const firstValueArgs = needsFirstValue(newRelation) ? undefined : { value: undefined };
  const secondValueArgs = needsSecondValue(newRelation) ? undefined : { secondValue: undefined };
  const relationArgs = newRelation === relation ? undefined : { relation: newRelation };

  return onChange({ columnType, ...firstValueArgs, ...secondValueArgs, ...relationArgs });
}

/**
 * Helper for updating a relation. This requires additional logic because in some cases
 * by changing the relation we'll have to set the first or second value to undefined.
 */
export function updateRelation(
  relation: Relation,
  onChange: (_: Partial<ClientRowFilter>) => void,
  needsFirstValue: (_: Relation) => boolean,
  needsSecondValue: (_: Relation) => boolean
) {
  const firstValueArgs = needsFirstValue(relation) ? undefined : { value: undefined };
  const secondValueArgs = needsSecondValue(relation) ? undefined : { secondValue: undefined };
  return onChange({ relation, ...firstValueArgs, ...secondValueArgs });
}

/**
 * Get the subset of relations that aligns with the current column type.
 */
export function getRelations(columnType: ColumnType) {
  if (columnType === ColumnType.BOOLEAN) {
    return BooleanRelations;
  } else if (columnType === ColumnType.DATE) {
    return DateRelations;
  } else if (columnType === ColumnType.NUMBER) {
    return NumberRelations;
  } else if (columnType === ColumnType.STRING) {
    return StringRelations;
  } else {
    return [] as any[];
  }
}

/**
 * Get a human readable version of the relation text (this includes updating the letters' case and adding
 * "is" to the beginning in certain cases).
 */
export function getStringifiedRelation(relation: string) {
  const words = _.lowerCase(relation).split(" ");
  if (words[words.length - 1] === "inclusive" || words[words.length - 1] === "exclusive") {
    words[words.length - 1] = `(${words[words.length - 1]})`;
  }

  if (words[0] !== "is" && words[0] !== "contains" && words[0] !== "starts" && words[0] !== "ends" && words[0] !== "does") {
    words.unshift("is");
  }

  return words.join(" ");
}

/**
 * Attempts to instantiate a row filter subclass based on the given row filter contents. If this instantiation fails,
 * we will add the cause of the failure to the signal edit forms errors dictionary.
 */
export function hasError(rowFilter: ClientRowFilter) {
  if (!rowFilter.columnType) {
    return true;
  }
  if (!rowFilter.relation) {
    return true;
  }

  // make sure that value and secondValue are defined properly based on relation
  if (needsFirstValue(rowFilter.relation)) {
    if (!rowFilter.value) {
      return true;
    }
  } else {
    if (rowFilter.value) {
      return true;
    }
  }
  if (needsSecondValue(rowFilter.relation)) {
    if (!rowFilter.secondValue) {
      return true;
    }
  } else {
    if (rowFilter.secondValue) {
      return true;
    }
  }

  switch (rowFilter.columnType) {
    case ColumnType.BOOLEAN:
      if (rowFilter.secondValue) {
        return true;
      }
      // Check that relation is valid
      if (!new Set(BooleanRelations as unknown as string[]).has(rowFilter.relation)) {
        return true;
      }
      break;

    case ColumnType.DATE:
      if (!new Set(DateRelations as unknown as string[]).has(rowFilter.relation)) {
        return true;
      }
      break;
    case ColumnType.NUMBER:
      if (!new Set(NumberRelations as unknown as string[]).has(rowFilter.relation)) {
        return true;
      }
      break;
    case ColumnType.STRING:
      if (!new Set(StringRelations as unknown as string[]).has(rowFilter.relation)) {
        return true;
      }
      if (rowFilter.value === "") {
        return true;
      }
      break;
  }

  return false;
}

/**
 * A simple helper function for getting any errors present in either the row filter as a whole or a specific field
 * in the row filter.
 */
export function getError(errors: any, index: number, fieldName?: string) {
  if (!errors["rowFilters"] || !errors["rowFilters"][index]) {
    return;
  }

  if (fieldName) {
    return errors["rowFilters"][index][fieldName];
  } else {
    return errors["rowFilters"][index];
  }
}

/**
 * Check if we need a second value based on the current relation
 */
export function needsSecondValue(relation: Relation) {
  return relation === Relation.BETWEEN_INCLUSIVE;
}

/**
 * Check if we need a first value based on the current relation
 */
export function needsFirstValue(relation: Relation) {
  return relation !== Relation.IS_EMPTY && relation !== Relation.NOT_EMPTY;
}
